import { Loader2Icon } from "lucide-react"
import { createContext, use, useRef, useState, type ReactNode } from "react"
import { Button } from "@/primitives/button.tsx"
import { Drawer, DrawerContent, DrawerTitle } from "@/primitives/drawer.tsx"
import { DestructiveConfirmation } from "./destructive-confirmation.tsx"
import { useOverlayForm } from "./use-overlay-form.ts"

/*
 * Writing a long text where it stands: a ticket's description, a comment its author edits. On the
 * desk it is the caller's editor, inline in place of what it edits (`view`), exactly as before. On
 * `compact` it is a full-screen task over the page, which keeps `view` beneath: Cancel, the title
 * and Save along the top, the editor filling the screen under them, and the editor's formatting
 * row docked on the on-screen keyboard (a `MarkdownEditor` inside docks it itself, through
 * `useInEditorSheet`). The tab bar steps aside while it is open.
 *
 * Which form it takes is chosen as editing starts and kept until it ends, so a window resized
 * across 1024px never swaps the editor under the Member's hands. `children` renders the editor and
 * whatever goes with it (a warning that someone changed the text, the uploads on their way), told
 * whether it is in the sheet, so the caller leaves out the Cancel and Save it shows on the desk.
 *
 * Closing the sheet on what the Member wrote and hasn't saved (`dirty`) asks "Discard changes?"
 * first, however it closes: Cancel, Back, Esc, or a swipe down. A caller's own Esc shortcut steps
 * aside for it (`standsInEditorSheet`). Saving is the caller's: `save` says what
 * its button reads, whether it can be pressed, and whether a save is under way.
 */

/** Whether what renders inside stands in an EditorSheet on `compact`. */
const EditorSheetContext = createContext(false)

/**
 * Whether this stands in an EditorSheet, or another full screen that writes (`EditorFillsScreen`),
 * on `compact`: a MarkdownEditor fills it and docks its toolbar.
 */
export const useInEditorSheet = () => use(EditorSheetContext)

/** A full screen on `compact` whose MarkdownEditor fills it as an EditorSheet's: the Composer's. */
export function EditorFillsScreen({ children }: { children: ReactNode }) {
  return <EditorSheetContext value>{children}</EditorSheetContext>
}

/**
 * Whether an element (a caller's form) stands in an EditorSheet on `compact`. Esc there is the
 * sheet's, closing it as Cancel does, so a caller's own Esc, which drops the text without asking,
 * passes it by.
 */
export const standsInEditorSheet = (element: Element | null) =>
  element?.closest("[data-editor-sheet]") != null

export type EditorSave = {
  /** What the button reads: "Save", or "Replace theirs" once someone else's text has been seen. */
  label: string
  onSave: () => void
  disabled: boolean
  pending: boolean
}

export function EditorSheet({
  open,
  title,
  view,
  dirty,
  onCancel,
  save,
  children,
}: {
  /** Whether the text is being edited. */
  open: boolean
  /** What the sheet edits, as its top bar names it: "Description", "Edit comment". */
  title: string
  /** What is edited, as the page shows it while nobody edits it, and beneath the sheet. */
  view: ReactNode
  /** Whether closing would throw away what the Member wrote. */
  dirty: boolean
  /** Ends editing, putting the text back as it was. */
  onCancel: () => void
  save: EditorSave
  children: (inSheet: boolean) => ReactNode
}) {
  const { docked } = useOverlayForm({ open })
  const [asking, setAsking] = useState(false)
  // What the sheet held as it closes, so it slides away with its text rather than empty.
  const shown = useRef<ReactNode>(null)

  if (!docked) return open ? children(false) : view
  if (open) shown.current = children(true)

  const close = () => {
    if (dirty) setAsking(true)
    else onCancel()
  }

  return (
    <>
      {view}
      <Drawer open={open} onOpenChange={(next) => !next && close()}>
        <DrawerContent
          size="screen"
          // The editor takes the focus for itself, at the end of the text.
          initialFocus={false}
        >
          {/* A press on the bar keeps the focus in the editor, as the Composer's does, so the
              keyboard stays up and the sheet, which rides it, holds still under the finger. Were
              the focus to leave, the drawer would drop its keyboard inset at once while the sheet
              eased down after it, and the press's end would land beside Save rather than on it. */}
          <div
            onMouseDown={(event) => event.preventDefault()}
            className="sticky top-0 z-10 flex h-12 shrink-0 items-center gap-2 border-b border-border-subtle bg-background px-4"
          >
            <Button variant="ghost" size="sm" disabled={save.pending} onClick={close}>
              Cancel
            </Button>
            <DrawerTitle className="min-w-0 flex-1 truncate text-center">{title}</DrawerTitle>
            <Button
              size="sm"
              disabled={save.disabled}
              focusableWhenDisabled={save.pending}
              onClick={save.onSave}
            >
              {save.pending && <Loader2Icon aria-hidden className="animate-spin" />}
              {save.label}
            </Button>
          </div>
          <EditorSheetContext value>
            {/* What the caller renders (its form) fills the screen under the bar. */}
            <div
              data-editor-sheet=""
              className="flex min-h-0 flex-1 flex-col *:flex *:min-h-0 *:flex-1 *:flex-col"
            >
              {shown.current}
            </div>
          </EditorSheetContext>
        </DrawerContent>
      </Drawer>
      <DestructiveConfirmation
        removes="unsaved-text"
        trigger="none"
        open={asking}
        onOpenChange={setAsking}
        onConfirm={async () => onCancel()}
      />
    </>
  )
}
